"use client";;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:ENCON_SENTRY_KEY":true});}catch(e){}}();
import type React from "react";
import {useMemo, useState} from "react";
import {usePathname} from "next/navigation";
import {Card, type CardCaseData, type CardInsightData, type CardJobData,} from "@/components/Card";
import {CMSLink, type CMSLinkType} from "@/components/Link";
import RichText from "@/components/RichText";
import Slider from "@/components/Slider";
import {Pagination} from "../Pagination";

export type Props = {
	insights: CardInsightData[];
	cases: CardCaseData[];
	jobs?: CardJobData[];
	categoryImages?: any;
	enableLink?: boolean | null | undefined;
	richText?: any;
	link?: CMSLinkType;
	view?: "grid" | "slider" | "archive" | undefined;
	blockId?: string;
	page?: number;
	onPageChange?: (page: number) => void; // Add optional prop from parent
};

const MAX_ITEMS_PER_PAGE = 12;

export const CollectionArchive: React.FC<Props> = (props) => {
	const {
		insights: rawInsights,
		cases: rawCases,
		jobs,
		categoryImages,
		view,
		richText,
		link,
		enableLink,
		blockId,
		page: pageFromParent,
		onPageChange: onPageChangeFromParent,
	} = props;
	const [internalPage, setInternalPage] = useState(pageFromParent ?? 1);
	const pathname = usePathname();
	const currentSlug = pathname.split("/").pop() || "";

	// Exclude the current page from cases/insights lists to avoid showing
	// the current item in "related" archive blocks on detail pages
	const cases = useMemo(
		() => rawCases.filter((item) => item.slug !== currentSlug),
		[rawCases, currentSlug],
	);
	const insights = useMemo(
		() => rawInsights.filter((item) => item.slug !== currentSlug),
		[rawInsights, currentSlug],
	);

	const requestedPage = pageFromParent ?? internalPage;
	const totalItems = Math.max(cases.length, insights.length);
	const totalPages = Math.max(1, Math.ceil(totalItems / MAX_ITEMS_PER_PAGE));
	const currentPage = Math.min(requestedPage, totalPages);

	// Calculate pagination slices
	const startIndex = (currentPage - 1) * MAX_ITEMS_PER_PAGE;
	const endIndex = startIndex + MAX_ITEMS_PER_PAGE;

	const paginatedInsights = insights?.slice(startIndex, endIndex) || [];
	const paginatedCases = cases?.slice(startIndex, endIndex) || [];

	const handlePageChange = (page: number) => {
		if (pageFromParent === undefined) {
			setInternalPage(page);
		}

		// If parent provided onPageChange, use it (for filtered views)
		if (onPageChangeFromParent) {
			onPageChangeFromParent(page);
			return;
		}

		// Otherwise handle scroll locally (for non-filtered views)
		if (blockId) {
			setTimeout(() => {
				const blockElement = document.getElementById(`block-${blockId}`);
				if (blockElement) {
					const headerOffset = 150;
					const elementPosition = blockElement.getBoundingClientRect().top;
					const offsetPosition =
						elementPosition + window.pageYOffset - headerOffset;

					window.scrollTo({
						top: offsetPosition,
						behavior: "smooth",
					});
				}
			}, 100);
		}
	};

	const caseSlides = cases.map((caseItem) => (
		<Card
			className="card"
			key={caseItem.slug}
			doc={caseItem}
			relationTo={"cases"}
			view={view}
		/>
	));

	return (
		<>
			{jobs && jobs.length > 0 && view === "archive" && (
				<div className={`archive_jobs 123 ${view}`}>
					<div>
						<RichText data={richText} />
						{enableLink && <CMSLink {...link} />}
					</div>
					{jobs.map((jobItem) => (
						<Card
							className="card"
							key={jobItem.slug}
							doc={jobItem}
							categoryImages={categoryImages}
							relationTo={"jobs"}
							view={view}
						/>
					))}
				</div>
			)}
			{insights &&
				insights.length > 0 &&
				(view === "grid" ? (
					<div className="archive_insights">
						{paginatedInsights.map((insight) => (
							<Card
								className="card"
								key={insight.slug}
								doc={insight}
								relationTo={"insights"}
								view={view}
							/>
						))}
					</div>
				) : view === "archive" ? (
					<>
						<div className={`archive_insights ${view}`}>
							<div>
								<RichText data={richText} />
								{enableLink && <CMSLink {...link} />}
							</div>
							{paginatedInsights.map((insight) => (
								<Card
									className="card"
									key={insight.slug}
									doc={insight}
									relationTo={"insights"}
									view={view}
								/>
							))}
						</div>
						<Pagination
							totalItems={insights.length}
							itemsPerPage={MAX_ITEMS_PER_PAGE}
							page={currentPage}
							onPageChange={handlePageChange}
							className="pagination__container"
						/>
					</>
				) : null)}
			{cases &&
				cases.length > 0 &&
				(view === "slider" ? (
					<div className={`archive_cases ${view}`}>
						<Slider
							slides={caseSlides}
							slidesPerView={1}
							pagination={{ clickable: true }}
							spaceBetween={32}
							breakpoints={{
								1024: { slidesPerView: 1.25, spaceBetween: 48 },
							}}
						/>
					</div>
				) : view === "archive" ? (
					<>
						<div className={`archive_cases ${view}`}>
							<div>
								<RichText data={richText} />
								{enableLink && <CMSLink {...link} />}
							</div>
							{paginatedCases.map((caseItem) => (
								<Card
									className="card"
									key={caseItem.slug}
									doc={caseItem}
									relationTo={"cases"}
									view={view}
								/>
							))}
						</div>
						<Pagination
							totalItems={cases.length}
							itemsPerPage={MAX_ITEMS_PER_PAGE}
							page={currentPage}
							onPageChange={handlePageChange}
							className="pagination__container"
						/>
					</>
				) : view === "grid" ? (
					<div className="archive_cases">
						{paginatedCases.map((caseItem) => (
							<Card
								className="card"
								key={caseItem.slug}
								doc={caseItem}
								relationTo={"cases"}
								view={view}
							/>
						))}
					</div>
				) : null)}
		</>
	);
};
